import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import { toast } from 'sonner';
import { 
  CreditCard, 
  Plus,
  Trash2,
  Lock,
  AlertTriangle,
  X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

// Bank partner colors for card styling
const BANK_COLORS = {
  gcash: { bg: 'from-[#007DFE] to-[#0052A3]', text: 'GCash' },
  maya: { bg: 'from-[#00D084] to-[#009960]', text: 'Maya' },
  grabpay: { bg: 'from-[#00B14F] to-[#008039]', text: 'GrabPay' },
  bdo: { bg: 'from-[#0066B3] to-[#004480]', text: 'BDO' },
  bpi: { bg: 'from-[#A81C32] to-[#7A1525]', text: 'BPI' },
  unionbank: { bg: 'from-[#FF6B00] to-[#CC5500]', text: 'UnionBank' },
};

// Card component that looks like a real card
const CardTile = ({ card, onDelete, panicMode, bankCode }) => {
  // Use bankCode prop (from grouping) or fallback to bank_name to find colors
  const code = bankCode || card.bank_name?.toLowerCase() || '';
  const bankConfig = BANK_COLORS[code] || { bg: 'from-[#2BBBAD] to-[#1A7A70]', text: card.bank_name || 'Bank' };
  const isLocked = card.status === 'panic_locked';
  
  return (
    <div 
      className={`
        relative w-full max-w-[340px] aspect-[1.586/1] rounded-2xl p-6
        bg-gradient-to-br ${bankConfig.bg}
        ${isLocked ? 'ring-2 ring-[#FF3B30] ring-offset-2 ring-offset-[#0B1C2D]' : ''}
        glass-card overflow-hidden transition-transform hover:scale-[1.02]
      `}
      data-testid={`card-${card.last4}`}
    >
      {/* Glass overlay effect */}
      <div className="absolute inset-0 bg-white/5 backdrop-blur-[1px]" />
      
      {/* Lock overlay for locked cards */}
      {isLocked && (
        <div className="absolute inset-0 bg-[#FF3B30]/20 flex items-center justify-center z-10">
          <div className="bg-[#FF3B30] text-white px-4 py-2 rounded-lg flex items-center gap-2 shadow-lg">
            <Lock size={18} />
            <span className="font-bold text-sm">PANIC LOCKED</span>
          </div>
        </div>
      )}
      
      {/* Card content */}
      <div className="relative z-5 h-full flex flex-col justify-between">
        {/* Top row */}
        <div className="flex items-start justify-between">
          <div>
            <p className="text-white/80 text-xs uppercase tracking-wider">{bankConfig.text}</p>
            <p className="text-white font-bold text-lg mt-1">
              {card.nickname || `${card.card_type.charAt(0).toUpperCase() + card.card_type.slice(1)} Card`}
            </p>
          </div>
          {!isLocked && !panicMode && (
            <button
              onClick={() => onDelete(card)}
              className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white/80 hover:text-white transition-colors"
              data-testid={`delete-card-${card.last4}`}
            >
              <Trash2 size={16} />
            </button>
          )}
        </div>
        
        {/* Card number (masked) */}
        <div className="my-4">
          <p className="text-white/90 font-mono text-xl tracking-[0.25em]">
            •••• •••• •••• {card.last4}
          </p>
        </div>
        
        {/* Bottom row */}
        <div className="flex items-end justify-between">
          <div>
            <p className="text-white/60 text-xs uppercase">Type</p>
            <p className="text-white font-medium text-sm">
              {card.card_type.charAt(0).toUpperCase() + card.card_type.slice(1)}
            </p>
          </div>
          <div className="text-right">
            <p className="text-white/60 text-xs uppercase">Status</p>
            <p className={`font-bold text-sm ${isLocked ? 'text-[#FF3B30]' : 'text-[#64FFDA]'}`}>
              {isLocked ? 'Locked' : 'Active'}
            </p>
          </div>
          {/* Card chip icon */}
          <div className="w-10 h-8 rounded bg-gradient-to-br from-yellow-300/80 to-yellow-500/80 ml-4" />
        </div>
      </div>
      
      {/* Locked timestamp */}
      {isLocked && card.locked_at && (
        <div className="absolute bottom-2 left-2 text-[10px] text-white/60">
          Locked: {new Date(card.locked_at).toLocaleString()}
        </div>
      )}
    </div>
  );
};

export default function CardsPage() {
  const { user, api } = useAuth();
  const [cards, setCards] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // Form state
  const [formBank, setFormBank] = useState('');
  const [formType, setFormType] = useState('');
  const [formLast4, setFormLast4] = useState('');
  const [formNickname, setFormNickname] = useState('');
  const [adding, setAdding] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [cardsRes, partnersRes] = await Promise.all([
        api.get('/cards'),
        api.get('/partners'),
      ]);
      setCards(cardsRes.data.cards);
      // Only show bank partners (exclude e-wallets for card addition)
      const bankPartners = partnersRes.data.partners.filter(p => 
        ['bdo', 'bpi', 'unionbank'].includes(p.code)
      );
      setPartners(bankPartners);
    } catch (error) {
      toast.error('Failed to load cards');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAddCard = async () => {
    if (!formBank || !formType || formLast4.length !== 4) {
      toast.error('Please fill in all required fields');
      return;
    }

    setAdding(true);
    try {
      await api.post('/cards', {
        bank_partner_id: formBank,
        card_type: formType,
        last4: formLast4,
        nickname: formNickname || null,
      });
      toast.success('Card added successfully');
      setShowAddModal(false);
      resetForm();
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add card');
    } finally {
      setAdding(false);
    }
  };

  const handleDeleteCard = async () => {
    if (!selectedCard) return;
    
    setDeleting(true);
    try {
      await api.delete(`/cards/${selectedCard.id}`);
      toast.success('Card removed');
      setShowDeleteModal(false);
      setSelectedCard(null);
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete card');
    } finally {
      setDeleting(false);
    }
  };

  const openDeleteModal = (card) => {
    setSelectedCard(card);
    setShowDeleteModal(true);
  };

  const resetForm = () => {
    setFormBank('');
    setFormType('');
    setFormLast4('');
    setFormNickname('');
  };

  // Group cards by bank - use bank_name for grouping (from partners data)
  const cardsByBank = cards.reduce((acc, card) => {
    // Find the partner to get the code for proper grouping
    const partner = partners.find(p => p.id === card.bank_partner_id);
    const bankCode = partner?.code || card.bank_partner_id;
    if (!acc[bankCode]) {
      acc[bankCode] = [];
    }
    acc[bankCode].push(card);
    return acc;
  }, {});

  const activeCards = cards.filter(c => c.status === 'active');
  const lockedCards = cards.filter(c => c.status === 'panic_locked');

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="spinner w-12 h-12"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8" data-testid="cards-page">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-3xl lg:text-4xl font-black text-[#E6F1FF]">My Cards</h1>
            <p className="text-[#8892B0] mt-1">Manage your card references for emergency protection</p>
          </div>
          
          <Button
            onClick={() => setShowAddModal(true)}
            disabled={user?.panic_mode}
            className="bg-[#2BBBAD] hover:bg-[#3FD2C7] text-[#0B1C2D] font-bold px-6 py-3 rounded-xl"
            data-testid="add-card-button"
          >
            <Plus className="w-5 h-5 mr-2" />
            Add Card
          </Button>
        </div>

        {/* Stats */}
        <div className="flex flex-wrap gap-4">
          <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#2BBBAD]/20 border border-[#2BBBAD]/30">
            <CreditCard size={16} className="text-[#2BBBAD]" />
            <span className="text-sm text-[#E6F1FF]">{cards.length} Total Cards</span>
          </div>
          <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#64FFDA]/20 border border-[#64FFDA]/30">
            <CreditCard size={16} className="text-[#64FFDA]" />
            <span className="text-sm text-[#E6F1FF]">{activeCards.length} Active</span>
          </div>
          {lockedCards.length > 0 && (
            <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#FF3B30]/20 border border-[#FF3B30]/30">
              <Lock size={16} className="text-[#FF3B30]" />
              <span className="text-sm text-[#FF3B30]">{lockedCards.length} Locked</span>
            </div>
          )}
        </div>

        {/* Cards Grid */}
        {cards.length === 0 ? (
          <div className="glass-card rounded-2xl p-12 text-center">
            <CreditCard className="w-16 h-16 mx-auto text-[#8892B0] mb-4" />
            <h3 className="text-xl font-bold text-[#E6F1FF] mb-2">No Cards Added</h3>
            <p className="text-[#8892B0] mb-6 max-w-md mx-auto">
              Add your bank cards to include them in your PanicLock protection. 
              Only the last 4 digits are stored for reference.
            </p>
            <Button
              onClick={() => setShowAddModal(true)}
              className="bg-[#2BBBAD] hover:bg-[#3FD2C7] text-[#0B1C2D] font-bold"
            >
              <Plus className="w-5 h-5 mr-2" />
              Add Your First Card
            </Button>
          </div>
        ) : (
          <div className="space-y-8">
            {Object.entries(cardsByBank).map(([bankCode, bankCards]) => (
              <div key={bankCode}>
                <h3 className="text-lg font-bold text-[#E6F1FF] mb-4 flex items-center gap-2">
                  <div 
                    className="w-8 h-8 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: BANK_COLORS[bankCode]?.bg.includes('007DFE') ? '#007DFE' : 
                             bankCode === 'bdo' ? '#0066B3' : 
                             bankCode === 'bpi' ? '#A81C32' : 
                             bankCode === 'unionbank' ? '#FF6B00' : '#2BBBAD' }}
                  >
                    <span className="text-white font-bold text-sm">
                      {BANK_COLORS[bankCode]?.text.charAt(0) || 'B'}
                    </span>
                  </div>
                  {BANK_COLORS[bankCode]?.text || bankCode}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                  {bankCards.map((card) => (
                    <CardTile 
                      key={card.id} 
                      card={card} 
                      bankCode={bankCode}
                      onDelete={openDeleteModal}
                      panicMode={user?.panic_mode}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Info Box */}
        <div className="glass-card rounded-2xl p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#3B82F6]/20 flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-6 h-6 text-[#3B82F6]" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-[#E6F1FF] mb-2">Security Notice</h3>
              <p className="text-sm text-[#8892B0]">
                <strong>This is not a PCI card storage system.</strong> Only the last 4 digits 
                and basic metadata are stored for emergency coordination purposes. No full card 
                numbers, CVV, expiry dates, or cardholder names are collected or stored.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Add Card Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="bg-[#112240] border-[#2BBBAD]/30 max-w-md">
          <DialogHeader>
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#2BBBAD]/20 flex items-center justify-center">
              <CreditCard className="w-8 h-8 text-[#2BBBAD]" />
            </div>
            <DialogTitle className="text-2xl font-black text-[#E6F1FF] text-center">
              Add Card Reference
            </DialogTitle>
            <DialogDescription className="text-center text-[#8892B0]">
              Add a card to include in your PanicLock protection. Only last 4 digits are stored.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-[#E6F1FF]">Bank *</Label>
              <Select value={formBank} onValueChange={setFormBank}>
                <SelectTrigger className="h-12 bg-[#0B1C2D] border-[#2BBBAD]/20 text-[#E6F1FF]">
                  <SelectValue placeholder="Select bank" />
                </SelectTrigger>
                <SelectContent className="bg-[#112240] border-[#2BBBAD]/20">
                  {partners.map((partner) => (
                    <SelectItem key={partner.id} value={partner.id} className="text-[#E6F1FF]">
                      {partner.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-[#E6F1FF]">Card Type *</Label>
              <Select value={formType} onValueChange={setFormType}>
                <SelectTrigger className="h-12 bg-[#0B1C2D] border-[#2BBBAD]/20 text-[#E6F1FF]">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent className="bg-[#112240] border-[#2BBBAD]/20">
                  <SelectItem value="credit" className="text-[#E6F1FF]">Credit Card</SelectItem>
                  <SelectItem value="debit" className="text-[#E6F1FF]">Debit Card</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-[#E6F1FF]">Last 4 Digits *</Label>
              <Input
                type="text"
                value={formLast4}
                onChange={(e) => setFormLast4(e.target.value.replace(/\D/g, '').slice(0, 4))}
                placeholder="1234"
                maxLength={4}
                className="h-12 text-center text-2xl tracking-widest bg-[#0B1C2D] border-[#2BBBAD]/20 text-[#E6F1FF] font-mono"
                data-testid="card-last4-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-[#E6F1FF]">Nickname (Optional)</Label>
              <Input
                type="text"
                value={formNickname}
                onChange={(e) => setFormNickname(e.target.value)}
                placeholder="e.g., BDO Platinum"
                className="h-12 bg-[#0B1C2D] border-[#2BBBAD]/20 text-[#E6F1FF]"
                data-testid="card-nickname-input"
              />
            </div>
          </div>

          <DialogFooter className="flex gap-3 sm:flex-row">
            <Button
              variant="outline"
              onClick={() => {
                setShowAddModal(false);
                resetForm();
              }}
              className="flex-1 border-[#64FFDA]/20 text-[#8892B0] hover:bg-[#64FFDA]/10"
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddCard}
              disabled={adding || !formBank || !formType || formLast4.length !== 4}
              className="flex-1 bg-[#2BBBAD] hover:bg-[#3FD2C7] text-[#0B1C2D] font-bold"
              data-testid="add-card-submit"
            >
              {adding ? 'Adding...' : 'Add Card'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="bg-[#112240] border-[#FF3B30]/30 max-w-md">
          <DialogHeader>
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#FF3B30]/20 flex items-center justify-center">
              <Trash2 className="w-8 h-8 text-[#FF3B30]" />
            </div>
            <DialogTitle className="text-xl font-bold text-[#E6F1FF] text-center">
              Remove Card?
            </DialogTitle>
            <DialogDescription className="text-center text-[#8892B0]">
              Are you sure you want to remove the card ending in {selectedCard?.last4}? 
              This will remove it from PanicLock protection.
            </DialogDescription>
          </DialogHeader>

          <DialogFooter className="flex gap-3 sm:flex-row">
            <Button
              variant="outline"
              onClick={() => {
                setShowDeleteModal(false);
                setSelectedCard(null);
              }}
              className="flex-1 border-[#64FFDA]/20 text-[#8892B0] hover:bg-[#64FFDA]/10"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteCard}
              disabled={deleting}
              className="flex-1 bg-[#FF3B30] hover:bg-[#E6352B] text-white font-bold"
              data-testid="confirm-delete-card"
            >
              {deleting ? 'Removing...' : 'Remove Card'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
